import { Outlet, Link, useLocation } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import Logo from "../assets/logo.png";
import { useI18n } from "../i18n";
import Footer from "./Footer";
import { navigationItems } from "../navigation";

const languageOptions = [
  { value: "hy", label: "Հայերեն" },
  { value: "ru", label: "Русский" },
  { value: "en", label: "English" },
];

/* =========================================
   LANGUAGE SELECTOR
========================================= */

const LanguageSelector = ({ language, setLanguage, t }) => {
  const [isOpen, setIsOpen] = useState(false);
  const selectorRef = useRef(null);

  const currentLanguage =
    languageOptions.find((option) => option.value === language) ||
    languageOptions[0];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        selectorRef.current &&
        !selectorRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, []);

  const handleLanguageChange = (value) => {
    setLanguage(value);
    setIsOpen(false);
  };

  return (
    <div ref={selectorRef} className="relative z-[60] shrink-0">
      {/* Selector button */}
      <button
        type="button"
        onClick={() => setIsOpen((open) => !open)}
        aria-label={t("accessibility.language")}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        className={`
          group relative flex h-11 items-center gap-2.5
          rounded-full border px-3.5
          border-slate-200 bg-slate-50 text-slate-800
          outline-none
          transition-all duration-300 ease-out
          hover:border-sky-300 hover:bg-sky-50
          focus-visible:ring-2 focus-visible:ring-sky-400
          ${
            isOpen
              ? "border-sky-500 bg-sky-50"
              : ""
          }
        `}
      >
        {/* Globe icon */}
        <svg
          aria-hidden="true"
          viewBox="0 0 24 24"
          fill="none"
          className="
            h-[18px] w-[18px] shrink-0
            text-sky-600
            transition-colors duration-300
            group-hover:text-sky-700
          "
        >
          <circle
            cx="12"
            cy="12"
            r="9"
            stroke="currentColor"
            strokeWidth="1.6"
          />

          <path
            d="M3 12h18M12 3a14 14 0 0 1 0 18m0-18a14 14 0 0 0 0 18"
            stroke="currentColor"
            strokeWidth="1.4"
          />
        </svg>

        {/* Current language */}
        <span className="whitespace-nowrap text-xs font-bold tracking-wider">
          {currentLanguage.value.toUpperCase()}
        </span>

        {/* Arrow */}
        <svg
          aria-hidden="true"
          viewBox="0 0 12 12"
          fill="none"
          className={`
            h-3 w-3 text-slate-400
            transition-all duration-300 ease-out
            ${
              isOpen
                ? "rotate-180 text-sky-500"
                : "group-hover:text-slate-700"
            }
          `}
        >
          <path
            d="m2.5 4.5 3.5 3 3.5-3"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {/* Dropdown */}
      <div
        className={`
          absolute right-0 top-[calc(100%+12px)] z-[100]
          w-44 origin-top-right
          overflow-hidden rounded-2xl
          border border-slate-200
          bg-white p-1.5
          shadow-[0_16px_40px_rgba(0,0,0,0.12)]
          transition-[opacity,transform,visibility]
          duration-300 ease-out
          ${
            isOpen
              ? "visible translate-y-0 scale-100 opacity-100"
              : "invisible -translate-y-2 scale-95 opacity-0"
          }
        `}
        role="listbox"
        aria-label={t("accessibility.language")}
        aria-hidden={!isOpen}
      >
        {/* Top highlight */}
        <div className="pointer-events-none absolute inset-x-5 top-0 h-px bg-gradient-to-r from-transparent via-sky-500/60 to-transparent" />

        {languageOptions.map((option, index) => {
          const isSelected = language === option.value;

          return (
            <button
              key={option.value}
              type="button"
              role="option"
              aria-selected={isSelected}
              tabIndex={isOpen ? 0 : -1}
              onClick={() => handleLanguageChange(option.value)}
              className={`
                group/item flex w-full items-center justify-between
                rounded-xl px-3 py-2.5 text-left
                transition-[background-color,color,transform]
                duration-200 ease-out
                ${
                  isSelected
                    ? "bg-sky-50 text-sky-700"
                    : "text-slate-600 hover:translate-x-0.5 hover:bg-slate-100 hover:text-slate-950"
                }
              `}
              style={{
                transitionDelay: isOpen ? `${index * 35}ms` : "0ms",
              }}
            >
              <span className="text-sm font-medium">
                {option.label}
              </span>

              {/* Selected indicator */}
              <span
                className={`
                  flex h-[18px] w-[18px] shrink-0
                  items-center justify-center rounded-full border
                  transition-all duration-200
                  ${
                    isSelected
                      ? "border-sky-500 bg-sky-50 text-sky-600"
                      : "border-slate-300 text-transparent group-hover/item:border-slate-400"
                  }
                `}
              >
                {isSelected && (
                  <svg
                    aria-hidden="true"
                    viewBox="0 0 12 12"
                    fill="none"
                    className="h-3 w-3"
                  >
                    <path
                      d="m2.5 6 2.2 2.2L9.5 3.5"
                      stroke="currentColor"
                      strokeWidth="1.7"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

/* =========================================
   HEADER
========================================= */

const Header = () => {
  const location = useLocation();

  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const { language, setLanguage, t } = useI18n();

  /* Close mobile menu after navigation */
  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  /* Navigation link styles */
  const linkClass = (path) => {
    const isActive =
      location.pathname === path ||
      (path !== "/" && location.pathname.startsWith(`${path}/`));

    return isActive
      ? "bg-blue-600 text-white shadow-md shadow-blue-600/20"
      : "text-slate-700 hover:bg-slate-100 hover:text-slate-950";
  };

  return (
    <>
      {/* Header */}
      <header
        className="
          fixed left-1/2 top-4 z-50
          w-[calc(100%-2rem)] max-w-5xl
          -translate-x-1/2
          rounded-3xl
          border-2 border-slate-400
          bg-white
          shadow-[0_15px_35px_rgba(15,23,42,0.30)]
          transition-all duration-300 ease-in-out
          md:top-6 md:w-max md:rounded-full md:px-3 md:py-1 
        "
      >
        {/* Header content */}
        <div className="flex items-center justify-between gap-4 px-4 py-2 md:gap-10 md:px-0 md:py-0">
          {/* Logo */}
          <Link
            to="/"
            className="group flex shrink-0 items-center"
            onClick={() => setIsMenuOpen(false)}
          >
            <img
              src={Logo}
              alt={t("accessibility.logo")}
              className="
                h-auto w-20
                transition-transform duration-300
                group-hover:scale-105 group-hover:rotate-1
              "
            />
          </Link>

          {/* Desktop navigation */}
          <nav className="hidden items-center gap-1 md:flex">
            {navigationItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`
                  ${linkClass(item.path)}
                  rounded-full px-4 py-2
                  text-base font-medium
                  transition-all duration-300 ease-in-out
                  lg:px-5 lg:text-md
                `}
              >
                {t(item.key)}
              </Link>
            ))}
          </nav>

          {/* Right controls */}
          <div className="flex shrink-0 items-center gap-2">
            {/* Language selector */}
            <LanguageSelector
              language={language}
              setLanguage={setLanguage}
              t={t}
            />

            {/* Mobile menu button */}
            <button
              type="button"
              className="
                group relative flex h-11 w-11
                items-center justify-center rounded-full
                text-slate-800
                transition-colors duration-300
                hover:bg-slate-100
                focus-visible:outline-none
                focus-visible:ring-2 focus-visible:ring-blue-400
                md:hidden
              "
              aria-label={
                isMenuOpen
                  ? t("accessibility.closeMenu")
                  : t("accessibility.openMenu")
              }
              aria-expanded={isMenuOpen}
              aria-controls="mobile-navigation"
              onClick={() => setIsMenuOpen((open) => !open)}
            >
              <span className="sr-only">
                {isMenuOpen
                  ? t("accessibility.closeMenu")
                  : t("accessibility.openMenu")}
              </span>

              {/* Animated hamburger */}
              <span
                className="relative h-5 w-6"
                aria-hidden="true"
              >
                <span
                  className={`
                    absolute left-0 top-0
                    h-0.5 w-6 rounded-full bg-current
                    transition-all duration-300 ease-in-out
                    ${
                      isMenuOpen
                        ? "top-[9px] rotate-45"
                        : ""
                    }
                  `}
                />

                <span
                  className={`
                    absolute left-0 top-[9px]
                    h-0.5 w-6 rounded-full bg-current
                    transition-all duration-200 ease-in-out
                    ${
                      isMenuOpen
                        ? "scale-x-0 opacity-0"
                        : "scale-x-100 opacity-100"
                    }
                  `}
                />

                <span
                  className={`
                    absolute left-0 top-[18px]
                    h-0.5 w-6 rounded-full bg-current
                    transition-all duration-300 ease-in-out
                    ${
                      isMenuOpen
                        ? "top-[9px] -rotate-45"
                        : ""
                    }
                  `}
                />
              </span>
            </button>
          </div>
        </div>

        {/* Mobile navigation */}
        <div
          id="mobile-navigation"
          aria-hidden={!isMenuOpen}
          inert={!isMenuOpen}
          className={`
            grid overflow-hidden
            transition-[grid-template-rows,opacity]
            duration-500 ease-in-out
            md:hidden
            ${
              isMenuOpen
                ? "grid-rows-[1fr] opacity-100"
                : "pointer-events-none grid-rows-[0fr] opacity-0"
            }
          `}
        >
          <nav className="min-h-0">
            <div className="flex flex-col gap-1 border-t border-slate-200 px-3 pb-3 pt-2">
              {navigationItems.map((item, index) => {
                const isActive =
                  location.pathname === item.path ||
                  (item.path !== "/" &&
                    location.pathname.startsWith(`${item.path}/`));

                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    tabIndex={isMenuOpen ? 0 : -1}
                    onClick={() => setIsMenuOpen(false)}
                    className={`
                      rounded-full px-4 py-3
                      text-base font-medium
                      transition-[opacity,transform,background-color,color]
                      duration-300 ease-out
                      ${
                        isActive
                          ? "bg-blue-600 text-white"
                          : "text-slate-700 hover:bg-slate-100 hover:text-slate-950"
                      }
                      ${
                        isMenuOpen
                          ? "translate-y-0 opacity-100"
                          : "-translate-y-2 opacity-0"
                      }
                    `}
                    style={{
                      transitionDelay: isMenuOpen
                        ? `${index * 40}ms`
                        : "0ms",
                    }}
                  >
                    {t(item.key)}
                  </Link>
                );
              })}
            </div>
          </nav>
        </div>
      </header>

      <Outlet />
      <Footer />
    </>
  );
};

export default Header;